import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Button } from '@/components/ui/button';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import type { Simulation, SimulationSchedule } from '@/modules/simulations/types';
import { setSidebarContent } from '@/redux/sidebar/actions';

import { EmptySimulations } from '../EmptySimulations';
import { SimulationsTable } from './SimulationsTable/SimulationsTable';

interface SimulationsListProps {
  brainId: string | undefined;
  simulations: Simulation[] | undefined;
  schedules: SimulationSchedule[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onDelete: (id: string) => void;
  onBulkDelete: (ids: string[]) => Promise<void>;
  onRun: (ids: string[]) => void;
  onRunAll: () => void;
}

export const SimulationsList = ({
  brainId,
  simulations,
  schedules,
  isLoading,
  isError,
  onDelete,
  onBulkDelete,
  onRun,
  onRunAll,
}: SimulationsListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { showEmpty, showEmptySkeleton, showList } = useEmptyStateView({
    scope: `simulations:${brainId}`,
    isLoading,
    isEmpty: !simulations || simulations.length === 0,
    isReady: !!brainId,
    isError,
  });

  if (showEmpty) {
    return (
      <EmptySimulations
        title={t('simulations.no_simulations_yet')}
        description={t('simulations.no_simulations_description')}
        action={
          <Button size="sm" onClick={() => dispatch(setSidebarContent('create_simulation'))}>
            <PlusIcon size={16} />
            {t('common.create')}
          </Button>
        }
      />
    );
  }

  if (showEmptySkeleton) {
    return <EmptySkeleton media="icon" descriptionLines={3} />;
  }

  if (!showList) {
    return null;
  }

  return (
    <SimulationsTable
      simulations={simulations ?? []}
      schedules={schedules}
      isLoading={isLoading}
      onRun={onRun}
      onRunAll={onRunAll}
      onDelete={onDelete}
      onBulkDelete={onBulkDelete}
    />
  );
};
